import React from 'react';
import { WifiIcon, CameraIcon, SettingsIcon, DropletIcon, LeafIcon, AlertTriangleIcon, ChevronRightIcon, ArrowUpIcon, BellIcon, SignalIcon, BatteryFullIcon } from 'lucide-react';
import { dataSources, floatingInsight, propertyAlert, phoneUi, type Bi } from '../../../data/waterSolution';
type P = {
  isFR: boolean;
};
const pk = (isFR: boolean) => (v: Bi) => isFR ? v.fr : v.en;
const CARD = 'rounded-2xl bg-white border border-[#EEEEF4] shadow-[0_20px_40px_-26px_rgba(30,30,80,0.45)]';
export const SOURCE_ICONS = {
  iot: WifiIcon,
  photo: CameraIcon,
  api: SettingsIcon
} as const;
export function SourcesCard({
  isFR
}: P) {
  const pick = pk(isFR);
  return <div className={`${CARD} p-5`} data-id="element-7311" data-mp="114mu2s45acli">
      <p className="font-sans text-[16px] font-semibold text-[#121A3A]" data-id="element-7312" data-mp="113lj0nn4pq3s">{pick(dataSources.title)}</p>
      <ul className="mt-4 space-y-4" data-id="element-7313" data-mp="de0rvhgtnmjf">
        {dataSources.items.map(s => {
        const Icon = SOURCE_ICONS[s.key];
        return <li key={s.key} className="flex items-center gap-3.5" data-id="element-7314" data-mp="8e4uw26jkvdd">
              <span className="w-10 h-10 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-7315" data-mp="4w609w46w6w7">
                <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={2} data-id="element-7316" data-mp="8vwobs2c08x1" />
              </span>
              <span data-id="element-7317" data-mp="drbxi6pe9gn4">
                <span className="block font-sans text-[13.5px] font-medium text-[#121A3A]" data-id="element-7318" data-mp="insw5uu7zdeg">{pick(s.title)}</span>
                <span className="block font-sans text-[12px] text-[#5B6478]" data-id="element-7319" data-mp="3i3pn85uc7lze">{pick(s.sub)}</span>
              </span>
            </li>;
      })}
      </ul>
    </div>;
}
export function InsightFloat({
  isFR
}: P) {
  const pick = pk(isFR);
  return <div className={`${CARD} overflow-hidden`} data-id="element-7320" data-mp="3r5yv0ny81193">
      <div className="flex items-center gap-3.5 px-5 py-4" data-id="element-7321" data-mp="3w38pwj4dq54b">
        <span className="w-11 h-11 rounded-full bg-[#EEF0FE] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-7322" data-mp="3w0mc41qwatdn">
          <DropletIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={2} data-id="element-7323" data-mp="1bc88uapvpjmm" />
        </span>
        <span data-id="element-7324" data-mp="locb4dimt43g">
          <span className="block font-sans text-[18px] font-semibold text-[#121A3A] leading-tight whitespace-nowrap" data-id="element-7325" data-mp="lnt4vu2cg459">{pick(floatingInsight.value)}</span>
          <span className="flex items-center gap-1 font-sans text-[13px] text-[#E5484D]" data-id="element-7326" data-mp="grlv1q6tu8i1">
            <ArrowUpIcon className="w-3 h-3" aria-hidden="true" data-id="element-7327" data-mp="vm0flv61hpyt" />
            {pick(floatingInsight.trend)}
          </span>
        </span>
      </div>
      <div className="flex items-center gap-3.5 px-5 py-3.5 bg-[#FAFAFD] border-t border-[#F0F0F6]" data-id="element-7328" data-mp="2xl1rbyn9dp2">
        <span className="w-9 h-9 rounded-full bg-[#E8F6EF] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-7329" data-mp="3u573v05pt83t">
          <LeafIcon className="w-4 h-4 text-[#1F9D7A]" strokeWidth={2} data-id="element-7330" data-mp="copj2ibslan2" />
        </span>
        <span data-id="element-7331" data-mp="7w653k970dhq">
          <span className="block font-sans text-[13px] font-medium text-[#121A3A]" data-id="element-7332" data-mp="7vmny5rd9t4r">{pick(floatingInsight.label)}</span>
          <span className="block font-sans text-[13px] text-[#5B6478]" data-id="element-7333" data-mp="3gawkyzgeg85o">{pick(floatingInsight.text)}</span>
        </span>
      </div>
    </div>;
}
export function PropertyAlertCard({
  isFR
}: P) {
  const pick = pk(isFR);
  return <div className={`${CARD} flex items-center gap-4 px-5 py-4`} data-id="element-7334" data-mp="1vmc2aci7vo95">
      <span className="w-12 h-12 rounded-full bg-[#FDECEC] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-7335" data-mp="1voosfcy4p4xa">
        <AlertTriangleIcon className="w-6 h-6 text-[#E5484D]" strokeWidth={2} data-id="element-7336" data-mp="1lwsin4mojqd8" />
      </span>
      <span className="flex-1 min-w-0" data-id="element-7337" data-mp="1lug5d4rva46i">
        <span className="block font-sans text-[15px] font-semibold text-[#121A3A]" data-id="element-7338" data-mp="29nepwtov16m6">{pick(propertyAlert.title)}</span>
        {propertyAlert.lines.map(l => <span key={l.en} className="block font-sans text-[13px] text-[#5B6478]" data-id="element-7339" data-mp="20lp01sammfqi">{pick(l)}</span>)}
      </span>
      <ChevronRightIcon className="w-4 h-4 text-[#121A3A] shrink-0" aria-hidden="true" data-id="element-7340" data-mp="24mqzoa8ggtt0" />
    </div>;
}
export function ConsumptionPhone({
  isFR
}: P) {
  const pick = pk(isFR);
  return <div className="w-[230px] rounded-[36px] bg-[#1B1D26] p-[7px] shadow-[0_40px_70px_-36px_rgba(20,20,60,0.6)]" data-id="element-7341" data-mp="t7vcfzvqtk0u">
      <div className="rounded-[30px] bg-white overflow-hidden" data-id="element-7342" data-mp="19frni70u6hyr">
        <div className="flex items-center justify-between px-5 pt-3 pb-1 font-sans text-[10px] font-semibold text-[#121A3A]" data-id="element-7343" data-mp="19h2vkj1bebvo">
          <span data-id="element-7344" data-mp="zox2t738nqff">9:41</span>
          <span className="w-16 h-4 rounded-full bg-[#1B1D26]" aria-hidden="true" data-id="element-7345" data-mp="zq86z80lj072" />
          <span className="flex items-center gap-1" aria-hidden="true" data-id="element-7346" data-mp="ur51jmgperyk">
            <SignalIcon className="w-3 h-3" data-id="element-7347" data-mp="1se43ocq60s3u" />
            <BatteryFullIcon className="w-3.5 h-3.5" data-id="element-7348" data-mp="1scszwcwpzkcq" />
          </span>
        </div>
        <div className="px-4 pb-4" data-id="element-7349" data-mp="1ng2j1nd3ckq9">
          <p className="font-sans text-[14px] font-semibold text-[#121A3A] mt-2" data-id="element-7350" data-mp="1nfu7pzwm9nh6">{pick(phoneUi.title)}</p>
          <ul className="flex gap-3 mt-2 font-sans text-[10px]" data-id="element-7351" data-mp="1ii0zl1br6je9">
            {phoneUi.tabs.map((t, i) => <li key={t.en} className={i === 0 ? 'text-[#4F46E5] font-semibold border-b-2 border-[#4F46E5] pb-0.5' : 'text-[#8A90A2]'} data-id="element-7352" data-mp="1egp4iga415yo" data-mpi={i}>
                {pick(t)}
              </li>)}
          </ul>
          <div className="mt-3 flex items-end gap-2 rounded-xl border border-[#EEEEF4] p-2.5" data-id="element-7353" data-mp="2emj4h7r9pcq8">
            <ul className="flex-1 flex items-end gap-1 h-[46px]" aria-hidden="true" data-id="element-7354" data-mp="2io4gyrzw3ozw">
              {phoneUi.bars.map((b, i) => <li key={i} className="flex-1 rounded-t-[2px]" style={{
              height: `${b / 100 * 46}px`,
              background: i === phoneUi.bars.length - 1 ? '#4F46E5' : '#DCDDFB'
            }} data-id="element-7355" data-mp="2nmzbusb4t40d" data-mpi={i} />)}
            </ul>
            <span className="text-right" data-id="element-7356" data-mp="2xbzb8ctjq1f3">
              <span className="block font-sans text-[16px] font-semibold text-[#121A3A] whitespace-nowrap" data-id="element-7357" data-mp="2xf52nypndfdz">{pick(phoneUi.value)}</span>
              <span className="block font-sans text-[9.5px] font-semibold text-[#E5484D]" data-id="element-7358" data-mp="36d794ts5h08n">↑ {pick(phoneUi.trend)}</span>
            </span>
          </div>
          <div className="mt-2.5 rounded-xl bg-[#FDF1F1] p-2.5 flex gap-2" data-id="element-7359" data-mp="3bc0jidc54b4l">
            <DropletIcon className="w-4 h-4 text-[#E5484D] shrink-0 mt-0.5" strokeWidth={2.2} aria-hidden="true" data-id="element-7360" data-mp="1c0zfdjrkepeg" />
            <span data-id="element-7361" data-mp="1gz11kdkaf4u5">
              <span className="block font-sans text-[10.5px] font-semibold text-[#D23B40] leading-tight" data-id="element-7362" data-mp="1h0ltrwj4qbou">{pick(phoneUi.alertTitle)}</span>
              <span className="block font-sans text-[9.5px] text-[#5B6478] mt-1 leading-snug" data-id="element-7363" data-mp="2fbdbub30yj0e">{pick(phoneUi.alertText)}</span>
            </span>
          </div>
          <div className="mt-2 rounded-xl border border-[#EEEEF4] p-2.5 flex gap-2" data-id="element-7364" data-mp="2t809kg2sxd7d">
            <LeafIcon className="w-4 h-4 text-[#1F9D7A] shrink-0 mt-0.5" strokeWidth={2.2} aria-hidden="true" data-id="element-7365" data-mp="2ode7j3im0wzy" />
            <span data-id="element-7366" data-mp="3367mmntyzb43">
              <span className="block font-sans text-[10.5px] font-semibold text-[#121A3A]" data-id="element-7367" data-mp="2y9pk7um43oux">{pick(phoneUi.tipTitle)}</span>
              <span className="block font-sans text-[9.5px] text-[#5B6478]" data-id="element-7368" data-mp="3c146cabbg667">{pick(phoneUi.tipText)}</span>
            </span>
          </div>
          <span className="mt-3 flex items-center justify-center gap-1.5 rounded-xl bg-[#4F46E5] py-2.5 font-sans text-[11px] font-medium text-white" data-id="element-7369" data-mp="3802dcb7zsqco">
            <BellIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-7370" data-mp="3h0qiegfergdf" />
            {pick(phoneUi.cta)}
          </span>
        </div>
      </div>
    </div>;
}